/** One broken rule on one field of a validation failure. */
export interface FieldError {
  /** The rule that failed, e.g. `MustBePositive`. */
  readonly category: string;
  /** The value as sent, when the server echoes it (e.g. a bad format). */
  readonly text?: string;
}

/** Field name → every rule it broke. */
export type FieldErrors = Readonly<Record<string, readonly FieldError[]>>;

/** Values an error message needs, e.g. `{ fields: ['discountValue'] }` on `OFFER_LOCKED`. */
export type ErrorParams = Readonly<Record<string, string | readonly string[]>>;

export interface ApiErrorInit {
  readonly code?: string | null;
  readonly traceId?: string | null;
  readonly details?: readonly string[] | null;
  readonly retryAfterSeconds?: number | null;
  readonly fieldErrors?: FieldErrors | null;
  readonly params?: ErrorParams | null;
}

/**
 * A failed API call. Switch on `code` and map it to your own message; `message` is the
 * server's English `detail` and is for logs only, never for the UI.
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly code: string | null;
  readonly traceId: string | null;
  readonly details: readonly string[] | null;
  readonly retryAfterSeconds: number | null;
  /** The `errors` map of a validation failure, keyed by field name. */
  readonly fieldErrors: FieldErrors | null;
  /** The `params` object some errors add: strings or lists of strings, keyed by name. */
  readonly params: ErrorParams | null;

  constructor(
    readonly status: number,
    message: string,
    init: ApiErrorInit = {},
  ) {
    super(message);
    this.code = init.code ?? null;
    this.traceId = init.traceId ?? null;
    this.details = init.details ?? null;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.fieldErrors = init.fieldErrors ?? null;
    this.params = init.params ?? null;
  }
}
